import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { bookingApi, unwrap } from '@/lib/api/client';
import type { SubscriptionSearchModel } from '@/features/manage-subscriptions/subscriptions.api';

/**
 * Subscription sales — the aggregate side of the memberships business.
 *
 * From docs/booking-api.swagger.json (`MembershipDashboardDto`). Same
 * `MembershipSearchModel` body as the Subscriptions grid, so a figure here and
 * the rows behind it are always answering the same question.
 */

/** `MembershipPeopleStatsDto`. */
export interface MembershipPeopleStats {
  activeNow: number | null;
  newInRange: number | null;
  expiringIn30Days: number | null;
  expiredInRange: number | null;
  cancelledInRange: number | null;
  /** Seats a multi-member plan covers — never counted as sales. */
  memberSeats: number | null;
  uniqueSubscribers: number | null;
  autoRenewOn: number | null;
  totalAllTime: number | null;
}

/** `MembershipOriginStatsDto` — how the active ones came to exist. */
export interface MembershipOriginStats {
  selfPurchase: number | null;
  adminAssigned: number | null;
  sponsored: number | null;
  fromGift: number | null;
  memberSeat: number | null;
  paidOffline: number | null;
}

/** `MembershipMoneyStatsDto`. */
export interface MembershipMoneyStats {
  collected: number | null;
  collectedOnline: number | null;
  collectedOffline: number | null;
  refunded: number | null;
  voided: number | null;
  net: number | null;
  /** List price of memberships never billed at all. Not revenue. */
  unbilled: number | null;
  unbilledCount: number | null;
  unredeemedGiftRevenue: number | null;
  /** How much under list price was collected. Informational, not owed. */
  discountsGiven: number | null;
  collectedAllTime: number | null;
}

/** `MembershipMethodBreakdownDto`. */
export interface MembershipMethodBreakdown {
  method: number;
  count: number | null;
  amount: number | null;
}

/** `MembershipPlanRollupDto`. */
export interface MembershipPlanRollup {
  subscriptionId: string;
  nameEn: string | null;
  nameEs: string | null;
  internalName: string | null;
  locationNameEn: string | null;
  locationNameEs: string | null;
  listPrice: number | null;
  sold: number | null;
  active: number | null;
  paidOnline: number | null;
  paidOffline: number | null;
  sponsored: number | null;
  adminAssigned: number | null;
  /** Sold with nothing collected and no sponsorship. */
  unpaid: number | null;
  collected: number | null;
  unbilled: number | null;
  /** `limit > 0` is how the original derived "has a cap". */
  limit: number | null;
  acquired: number | null;
}

/** `MembershipDashboardDto`. */
export interface MembershipDashboard {
  people: MembershipPeopleStats | null;
  origin: MembershipOriginStats | null;
  money: MembershipMoneyStats | null;
  offlineByMethod: MembershipMethodBreakdown[] | null;
  plans: MembershipPlanRollup[] | null;
}

export function useMembershipDashboard(model: SubscriptionSearchModel) {
  return useQuery({
    queryKey: ['subscription-sales', 'dashboard', model],
    queryFn: () =>
      unwrap<MembershipDashboard>(
        bookingApi.post('v2/admin/memberships/dashboard', model)
      ),
    placeholderData: keepPreviousData,
  });
}
